import { useEffect, useState } from "react";
import { finance, formatErr } from "@/lib/api";
import { GasPump, RoadHorizon, Trash, Plus, CurrencyDollar } from "@phosphor-icons/react";
import { toast } from "sonner";

export const money = (n) => n == null ? "—" : `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function LoadMoney({ load, onChange }) {
  const [expenses, setExpenses] = useState([]);
  const refresh = () => finance.expenses(load.id).then(setExpenses).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { refresh(); }, [load.id]);
  const changed = () => { refresh(); onChange(); };
  return <section className="mb-8" data-testid="load-money">
    <h3 className="font-display font-bold text-xl mb-3">Load profit</h3>
    <ProfitStats f={load.finance} load={load} onChange={onChange}/>
    <div className="grid lg:grid-cols-2 gap-4 mt-4 items-start">
      <VehicleRates vehicles={load.vehicles || []} onChange={onChange}/>
      <Expenses loadId={load.id} expenses={expenses} onChange={changed}/>
    </div>
  </section>;
}

function ProfitStats({ f, load, onChange }) {
  const [miles, setMiles] = useState(load.loaded_miles ?? "");
  useEffect(() => { setMiles(load.loaded_miles ?? ""); }, [load.loaded_miles]);
  const saveMiles = async () => {
    const val = miles === "" ? null : parseFloat(miles);
    if (val === (load.loaded_miles ?? null)) { return; }
    try { await finance.setMiles(load.id, val); toast.success("Loaded miles saved"); onChange(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
    <Box k="Revenue" v={money(f.revenue)} tid="fin-revenue" sub={`${f.rated_vehicles}/${f.vehicle_count} vehicles rated`}/>
    <Box k="Fuel" v={money(f.fuel)} tid="fin-fuel"/>
    <Box k="Tolls" v={money(f.tolls)} tid="fin-tolls"/>
    <div className={`p-3 border-2 ${f.profit < 0 ? "border-red-600 bg-red-50" : "border-primary bg-primary text-primary-foreground"}`} data-testid="fin-profit">
      <div className="eyebrow opacity-80">Profit</div>
      <div className="font-display font-black text-2xl">{money(f.profit)}</div>
    </div>
    <label className="bg-white border border-border/60 p-3 block">
      <span className="eyebrow text-muted-foreground block">Loaded miles</span>
      <input data-testid="in-loaded-miles" type="number" min="0" inputMode="decimal" value={miles} placeholder="0"
        onChange={(e) => setMiles(e.target.value)} onBlur={saveMiles} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className="w-full font-display font-bold text-lg border-b-2 border-border focus:border-accent outline-none bg-transparent"/>
    </label>
    <Box k="Profit / mile" v={f.profit_per_mile == null ? "—" : money(f.profit_per_mile)} tid="fin-ppm"
      sub={f.revenue_per_mile == null ? "add miles" : `${money(f.revenue_per_mile)} revenue / mi`}/>
  </div>;
}

function Box({ k, v, tid, sub }) {
  return <div className="bg-white border border-border/60 p-3" data-testid={tid}>
    <div className="eyebrow text-muted-foreground">{k}</div>
    <div className="font-display font-bold text-lg">{v}</div>
    {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
  </div>;
}

function VehicleRates({ vehicles, onChange }) {
  return <div className="bg-white border border-border/60 p-4">
    <div className="eyebrow mb-3 flex items-center gap-1"><CurrencyDollar size={14}/> Rate per vehicle</div>
    {vehicles.length === 0 && <div className="text-sm text-muted-foreground">Add vehicles to set their rates.</div>}
    <div className="divide-y divide-border/60">
      {vehicles.map(v => <RateRow key={v.id} v={v} onChange={onChange}/>)}
    </div>
  </div>;
}

function RateRow({ v, onChange }) {
  const [rate, setRate] = useState(v.rate ?? "");
  const save = async () => {
    const val = rate === "" ? null : parseFloat(rate);
    if (val === (v.rate ?? null)) { return; }
    try { await finance.setRate(v.id, val); toast.success("Rate saved"); onChange(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return <div className="flex items-center gap-3 py-2">
    <div className="flex-1 min-w-0">
      <div className="font-display font-bold truncate">{v.year} {v.make} {v.model}</div>
      <div className="font-mono-tech text-[11px] text-muted-foreground truncate">{v.vin}</div>
    </div>
    <div className="flex items-center border-2 border-border focus-within:border-accent">
      <span className="pl-2 text-muted-foreground">$</span>
      <input data-testid={`in-rate-${v.id}`} type="number" min="0" inputMode="decimal" value={rate} placeholder="0.00"
        onChange={(e) => setRate(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className="w-24 px-2 py-1.5 outline-none text-right font-mono-tech"/>
    </div>
  </div>;
}

function Expenses({ loadId, expenses, onChange }) {
  const del = async (id) => {
    try { await finance.delExpense(id); toast.success("Receipt removed"); onChange(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return <div className="bg-white border border-border/60 p-4">
    <div className="eyebrow mb-3">Fuel & toll receipts</div>
    <ExpenseForm loadId={loadId} onAdded={onChange}/>
    {expenses.length === 0 && <div className="text-sm text-muted-foreground mt-3" data-testid="expenses-empty">No receipts yet.</div>}
    <ul className="divide-y divide-border/60 mt-2" data-testid="expense-list">
      {expenses.map(e => {
        const Icon = e.kind === "fuel" ? GasPump : RoadHorizon;
        return <li key={e.id} className="flex items-center gap-3 py-2" data-testid={`expense-${e.id}`}>
          <Icon size={20} weight="duotone" className="text-primary shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold capitalize">{e.kind}{e.note ? <span className="font-normal text-muted-foreground"> · {e.note}</span> : null}</div>
            <div className="font-mono-tech text-[11px] text-muted-foreground">{e.date}</div>
          </div>
          <div className="font-mono-tech font-bold">{money(e.amount)}</div>
          <button onClick={() => del(e.id)} data-testid={`btn-del-expense-${e.id}`} aria-label="Remove receipt" className="text-muted-foreground hover:text-red-600 transition-colors"><Trash size={16}/></button>
        </li>;
      })}
    </ul>
  </div>;
}

function ExpenseForm({ loadId, onAdded }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ kind: "fuel", amount: "", date: today, note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(f.amount);
    if (!amount || amount <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try { await finance.addExpense(loadId, { ...f, amount, note: f.note || null }); setF({ ...f, amount: "", note: "" }); toast.success("Receipt added"); onAdded(); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };
  const cls = "border-2 border-border focus:border-accent px-2 py-2 text-sm outline-none bg-white min-w-0";
  return <form onSubmit={submit} className="grid grid-cols-2 sm:grid-cols-[auto_1fr_auto] gap-2">
    <select data-testid="in-expense-kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={cls}>
      <option value="fuel">Fuel</option><option value="toll">Toll</option>
    </select>
    <input data-testid="in-expense-amount" type="number" step="0.01" min="0" inputMode="decimal" placeholder="Amount $" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={cls}/>
    <input data-testid="in-expense-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={cls}/>
    <input data-testid="in-expense-note" placeholder="Note (station, plaza…)" value={f.note} maxLength={200} onChange={(e) => setF({ ...f, note: e.target.value })} className={`${cls} sm:col-span-2`}/>
    <button data-testid="btn-add-expense" disabled={busy} className="btn-amber col-span-2 sm:col-span-1"><Plus size={16} weight="bold"/> Add</button>
  </form>;
}
